/**
 * Collision, compaction and swap — the placement resolver (TV1-2291 · S8/S9).
 *
 * Ported from prototype B (`docs/prs/TV1-2291/prototypes/prototype-b-grid.html`,
 * `hits` / `compact` / `overlapCells` / `swapCandidate`), which is verified in a
 * browser. That prototype is the spec; this module is the same algorithm in
 * TypeScript, plus a termination guard the prototype does not have.
 *
 * Why the layout engine needs this at all: two widgets that touch USED to be
 * REFUSED — `noOverlapValidator` rejected the save rather than resolving the
 * situation, so a drag that landed one pixel into a neighbour was a dead end for
 * the user. Compaction resolves it instead: the neighbour is pushed down and
 * everything floats back up into the gap that opens. Once placement guarantees
 * non-overlap BY CONSTRUCTION the validator is vacuous, and (concern C1) it had
 * to be retired in the same slice that wired this in — a world with neither is
 * one where overlap is silently permitted. Both landed in S10: the validator is
 * deleted, and `WidgetLayoutEditor.resolvePlacement` is this module's only
 * caller on the write path.
 *
 * The module is deliberately pure: no React, no DOM, no imports from the
 * components around it. It is the piece that has to be provably correct, and it
 * should be testable in a node environment without a renderer.
 */

/**
 * A widget reduced to what placement cares about: an identity and a grid rect.
 *
 * `i` is load-bearing rather than decorative — every collision test has to
 * exclude the widget from itself, because the resolver routinely compares a
 * widget against a HYPOTHETICAL version of itself one row up.
 *
 * `width` and `height` are COLUMN and ROW SPANS, never pixels — pixels are
 * `gridGeometry.ts`'s job. The names deliberately match `ResolvedWidget`
 * (`autoGrid.ts`) and `PlacedWidget` (`gridGeometry.ts`): one folder, one
 * grammar. This module briefly spelled them `w`/`h`, which bought nothing and
 * would have cost a translation layer — and the bugs that live in translation
 * layers — at every call site that hands a resolved widget straight in.
 */
export type PlacementRect = {
  i: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Hard ceiling on the row a widget may be pushed to — the termination guard.
 *
 * The push-down search is *mathematically* bounded already: nothing can collide
 * at or below the bottom-most edge of everything placed so far, so the search
 * has a natural stopping row (see `compact`). That bound is only as trustworthy
 * as the heights it is derived from, and heights arrive from persisted JSON. A
 * `height` of `1e9` — a corrupt write, a bad import, an LLM-authored layout with
 * a fat-fingered exponent — makes the natural bound a billion rows, and the loop
 * then runs a billion times on the main thread. `height: Infinity` makes it
 * never terminate at all. Either way the tab is frozen while the user drags a
 * card, with no error and nothing to recover from.
 *
 * So the natural bound is clamped to this. 4096 rows is 163,840 CSS px at the
 * 40px row height — roughly sixty screens of report, which no real layout
 * approaches — so the clamp cannot bite on honest input.
 *
 * When it DOES bite, it prefers a possibly-overlapping placement to a frozen
 * browser. That is the right trade: pathological input has no valid placement to
 * find, and a visibly wrong card is recoverable where a hung tab is not.
 */
export const MAX_PLACEMENT_ROW = 4096;

/**
 * Do two rects overlap? Identity is excluded: a widget never collides with
 * itself, however it is spelled.
 *
 * Strict `<` / `>` on purpose — rects that merely touch (`a` ends on the row
 * `b` starts) do not collide. Using `<=` would make every compacted stack
 * collide with the neighbour it was just packed against.
 */
export const collides = ( a: PlacementRect, b: PlacementRect ): boolean =>
  a.i !== b.i &&
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;

/**
 * Placement order: topmost first, left-to-right within a row — except that the
 * priority widget wins every tie it is in.
 *
 * The priority exists for dragging. Mid-drag the widget under the pointer and
 * the widget it is being dropped onto are both at the same `y`, and whichever is
 * placed first keeps the row. Without the override the incumbent wins by virtue
 * of a smaller `x`, so the card refuses to go where it was dropped and snaps
 * back under its neighbour — the user's explicit intent losing to an accident of
 * column order. With it, the dragged card takes the row and the incumbent is
 * pushed down, which is what the gesture means.
 */
const byPlacementOrder =
  ( priorityId: string | null ) =>
    ( a: PlacementRect, b: PlacementRect ): number => {
      if ( a.y !== b.y ) return a.y - b.y;
      if ( a.i === priorityId ) return -1;
      if ( b.i === priorityId ) return 1;
      return a.x - b.x;
    };

/**
 * Where a widget starts its search.
 *
 * Non-finite and negative rows are normalised away here rather than tolerated.
 * `y` is optional on a layout widget and arrives from persisted JSON, and a
 * `NaN` row would sail straight through this module: every comparison against
 * `NaN` is `false`, so the widget collides with nothing, is placed on top of
 * everything, and the non-overlap guarantee this module exists to provide is
 * quietly void. That is the same trap `autoGrid.ts` documents for the validator
 * we are replacing — it must not survive the replacement.
 *
 * Rounding matches every upstream producer (`pxToRow`, `resolveLayout`): rows
 * are integers. A fractional start would also step off the bottom of the
 * float-up loop into a negative row.
 */
const startRow = ( y: number ): number =>
  Number.isFinite( y ) ? Math.max( 0, Math.round( y )) : 0;

/**
 * Resolve a set of widgets to a non-overlapping, gravity-packed layout.
 *
 * Each widget, in placement order, floats UP while the cell above it is free,
 * then is pushed DOWN until it clears everything already placed. Widgets are
 * only ever tested against widgets placed BEFORE them, which is what makes the
 * pass single and the result stable: an earlier widget is never disturbed by a
 * later one.
 *
 * `priorityId` — the widget under the pointer during a drag. See
 * `byPlacementOrder`: it wins ties at equal `y` so the user's intent beats
 * incumbency.
 *
 * Pure: the input array and its elements are untouched, and every returned
 * element is a fresh object. Idempotent: compacting a compacted layout is a
 * no-op. Extra fields on the input elements are carried through, so a caller can
 * hand this whole widgets and get whole widgets back.
 *
 * The returned array is in PLACEMENT order (top-to-bottom, then left-to-right),
 * which is not necessarily the input order.
 */
export const compact = <T extends PlacementRect>(
  items: readonly T[],
  priorityId: string | null = null,
  /**
   * Pull every card up into the first free row above it.
   *
   * ⚠️ **OFF for anything the user positioned** (2026-08-17). With gravity on, a
   * card dropped at row 8 of an otherwise-empty report is dragged straight back to
   * row 0 — reported as *"i cant move things below the first line if nothing is
   * there"*, and it is exactly what the float-up loop below does. Deliberate
   * whitespace was simply not expressible: you could drop a card anywhere you
   * liked and it went where the algorithm liked.
   *
   * The rule now is **gravity applies only to a card the user did not place**:
   *
   *   drag / resize / remove -> `false`. What you put somewhere stays there, and
   *                             removing a card leaves the hole it left rather
   *                             than re-flowing cards you never touched.
   *   add                    -> `true`. A new card has no position the user chose,
   *                             so floating it into the first hole that fits is
   *                             help, not interference.
   *
   * Collision resolution (the push-down below) is NOT optional either way — two
   * cards may never occupy a cell, whoever asked for it.
   */
  { gravity = true }: { gravity?: boolean } = {}
): T[] => {
  const ordered = items
    .map(( item ) => ({ ...item, y: startRow( item.y ) }))
    .sort( byPlacementOrder( priorityId ));

  const placed: T[] = [];

  for ( const item of ordered ) {
    // The bottom-most edge of everything placed so far. Below it nothing can
    // collide, which makes it both the highest row the push-down can ever need
    // and a safe row to begin the float-up from — so a widget parked at row
    // 900 in a layout only 6 rows deep costs one step, not 894. Clamped by the
    // termination guard; see `MAX_PLACEMENT_ROW`.
    const ceiling = Math.min(
      placed.reduce(( bottom, p ) => Math.max( bottom, p.y + p.height ), 0 ),
      MAX_PLACEMENT_ROW
    );

    // The ceiling clamp is part of GRAVITY, not of collision resolution: it drags
    // a card down to the bottom-most placed edge before the float-up starts. With
    // gravity off it would silently move a card the user parked below everything
    // else, which is the behaviour being removed.
    if ( gravity ) {
      item.y = Math.min( item.y, ceiling );

      // Float up. Closes the gap left by whatever used to be above.
      while ( item.y > 0 && !placed.some(( p ) => collides( p, { ...item, y: item.y - 1 }))) {
        item.y -= 1;
      }
    }

    // Push down: the collision resolution. `item.y <= ceiling` is pure guard —
    // on honest input the loop always exits on the collision test first, because
    // no rect placed so far reaches row `ceiling`.
    while ( item.y <= ceiling && placed.some(( p ) => collides( p, item ))) {
      item.y += 1;
    }

    placed.push( item );
  }

  return placed;
};

/**
 * Find the topmost row `incoming` fits in, WITHOUT MOVING ANYTHING ELSE.
 *
 * This is what ADD needs, and it is deliberately not `compact( …, { gravity: true })`
 * (2026-08-17). Global gravity would find the new card a home by dragging every
 * OTHER card up too — including ones parked low on purpose — which is precisely the
 * behaviour removed from drag and remove. The distinction the engine now keeps is
 * "who chose this position": a new card has no chosen position, so the editor picks
 * one; every existing card does, so it keeps it.
 *
 * A first-fit scan from row 0, so a new card lands in the first gap that will take
 * it rather than at the bottom of a report whose last card happens to sit at row 40.
 * Bounded by {@link MAX_PLACEMENT_ROW} — the same termination guard `compact` uses,
 * for the same reason: `x`/`y`/`width`/`height` come from persisted JSON and a
 * pathological rect must not spin.
 */
export const placeWithoutMoving = <T extends PlacementRect>(
  existing: readonly T[],
  incoming: T
): T => {
  for ( let y = 0; y <= MAX_PLACEMENT_ROW; y++ ) {
    const candidate = { ...incoming, y };
    if ( !existing.some(( item ) => collides( item, candidate ))) return candidate;
  }
  return { ...incoming, y: MAX_PLACEMENT_ROW };
};

/**
 * Ratio at which a hovering card ARMS a swap, and the lower ratio at which an
 * already-armed swap DISARMS. Two numbers, not one, and the gap between them is
 * the whole point.
 *
 * A single threshold means the verdict is a step function of the pointer
 * position, and a pointer is never still. Resting anywhere near the boundary —
 * which is exactly where a user hovers while deciding — flips the preview
 * between "swap" and "push" on every stray pixel: the target card's highlight
 * strobes, and the compacted preview underneath re-shuffles every frame,
 * because swap and push produce genuinely different layouts. Hysteresis makes
 * the boundary depend on the current state, so crossing back out costs a real
 * gesture (down to 34%) rather than a tremor.
 *
 * The values are the prototype's, measured in a browser. Do not collapse them.
 */
export const SWAP_ARM_RATIO = 0.5;
export const SWAP_DISARM_RATIO = 0.34;

/**
 * Intersection AREA of two rects, in cells. Zero when they do not overlap.
 *
 * Pure geometry, and — unlike `collides` — deliberately IDENTITY-BLIND: a rect
 * against itself returns its own area, because that is what the intersection of
 * a region with itself is. Excluding self is the caller's job, and
 * `swapCandidate` does it explicitly. A caller that loops over a layout without
 * skipping the moving widget will therefore get a spurious 100% against itself;
 * that is the one trap here, and it is why the skip in `swapCandidate` is a
 * guard clause rather than a filter buried in a chain.
 *
 * Non-finite input propagates to `NaN` rather than being normalised. That is
 * intentional: `swapCandidate` treats a falsy/`NaN` overlap as "no overlap" and
 * falls through to push, which is the safe verdict for a corrupt rect. Swap
 * relocates two cards on a guess; push does not.
 */
export const overlapCells = ( a: PlacementRect, b: PlacementRect ): number => {
  const overlapX = Math.max(
    0,
    Math.min( a.x + a.width, b.x + b.width ) - Math.max( a.x, b.x )
  );
  const overlapY = Math.max(
    0,
    Math.min( a.y + a.height, b.y + b.height ) - Math.max( a.y, b.y )
  );

  return overlapX * overlapY;
};

/**
 * The verdict for one frame of a drag.
 *
 * `target`  — swap with this widget. `null` means push-and-compact instead.
 * `ratio`   — overlap with `nearest`, as a fraction of the SMALLER card's area.
 *             This is the number the UI renders as a percentage and a meter.
 * `nearest` — the most-overlapped widget, whether or not it is a valid target.
 *             Present so the UI can name the card the user is aiming at even
 *             when the answer is "push".
 * `sizeBlocked` — set only when the most-overlapped widget was rejected purely
 *             for having a different footprint. Without it the UI has no way to
 *             distinguish "you are not far enough onto it" from "you can never
 *             swap with this one", and the interaction just feels broken:
 *             the meter climbs to 100% and nothing happens. With it the UI can
 *             say "otro tamaño, empuja".
 */
export type SwapCandidate<T extends PlacementRect = PlacementRect> = {
  target: T | null;
  ratio: number;
  nearest: T | null;
  sizeBlocked?: boolean;
};

/**
 * Which widget — if any — the moving widget should SWAP with, rather than
 * displace.
 *
 * Three rules, each of which was got wrong first in the prototype and corrected
 * against measurement in a browser. None of them is incidental.
 *
 * 1. **The ratio is measured against the SMALLER of the two cards.** Measuring
 *    against the dragged card makes the rule ASYMMETRIC: at identical geometry,
 *    A dropped on B swaps while B dropped on A does not, because the same
 *    intersection is a large fraction of the small card and a small fraction of
 *    the large one. Users do not experience a drag as having a subject and an
 *    object — dragging A onto B and B onto A are the same gesture with the same
 *    intent — so an asymmetric rule reads as the feature randomly not working.
 *
 * 2. **Two thresholds, not one** — see `SWAP_ARM_RATIO` / `SWAP_DISARM_RATIO`.
 *    `armedId` is the target the caller is CURRENTLY previewing a swap with; it
 *    is what makes the gate stateful. Pass `null` when nothing is armed.
 *
 * 3. **Equal footprints only.** A swap between different-sized cards is not a
 *    swap: the smaller card lands in a hole the wrong shape, the compactor
 *    resolves the mess, and the layout detonates. Measured on the sample
 *    layout: a same-size swap relocates 2 widgets; a 3×3 tile swapped onto an
 *    8×6 chart relocates 7, because the chart lands in a hole five columns too
 *    narrow and everything beside it is displaced. The prototype's unrestricted
 *    mode exists ONLY to demonstrate that collateral — it is not a product
 *    option, and it is deliberately not ported.
 *
 * Mismatched pairs return `target: null` with `sizeBlocked: true`, so the caller
 * falls through to push-and-compact AND can explain why.
 *
 * Pure: nothing here is mutated, and the returned `target`/`nearest` are the
 * caller's own array elements by reference (not copies), so extra widget fields
 * survive untouched.
 */
export const swapCandidate = <T extends PlacementRect>(
  items: readonly T[],
  moving: PlacementRect,
  armedId: string | null = null
): SwapCandidate<T> => {
  let best: T | null = null;
  let bestRatio = 0;
  let blocked: T | null = null;
  let blockedRatio = 0;

  for ( const item of items ) {
    // `overlapCells` is identity-blind by design; the moving widget is still in
    // the layout array, and against itself it would score a perfect 1.
    if ( item.i === moving.i ) continue;

    const overlap = overlapCells( moving, item );
    // Also the `NaN` gate: a corrupt rect yields `NaN`, `!NaN` is `true`, and
    // the widget is skipped rather than swapped with on garbage.
    if ( !overlap ) continue;

    // Rule 1: the SMALLER card is the denominator, so the verdict does not
    // depend on which of the two the user happens to be holding.
    const ratio = overlap / Math.min(
      moving.width * moving.height,
      item.width * item.height
    );

    // Rule 3. Recorded rather than discarded: this is the card the user is
    // plainly aiming at, and the UI has to be able to say so.
    if ( item.width !== moving.width || item.height !== moving.height ) {
      if ( ratio > blockedRatio ) {
        blocked = item;
        blockedRatio = ratio;
      }
      continue;
    }

    if ( ratio > bestRatio ) {
      bestRatio = ratio;
      best = item;
    }
  }

  if ( !best ) {
    return blocked
      ? { target: null, ratio: blockedRatio, nearest: blocked, sizeBlocked: true }
      : { target: null, ratio: 0, nearest: null };
  }

  // Rule 2: the gate is lower for the target already being previewed, so a
  // hovering pointer cannot rattle the verdict back and forth across it.
  const gate = armedId === best.i ? SWAP_DISARM_RATIO : SWAP_ARM_RATIO;

  return { target: bestRatio >= gate ? best : null, ratio: bestRatio, nearest: best };
};
